import { on } from "@ngrx/store";
import { PagesStore } from "../pages.state";
import { QuoteActions } from "./quote.actions";
import { initialQuoteState } from "./quote.state";
import { getLatestVersionId, shouldShowRequirementsDialog } from "./quote.utils";
import { CreditCheckActions } from "../../application/data/credit-check/credit-check.actions";
import { QuoteVersionActions } from "./version/version.actions";
import { ArticlesActions } from "./articles/articles.actions";
import { articlesReducer } from "./articles/articles.reducer";
import { versionReducer } from "./version/version.reducer";
import { vendorInquiriesReducer } from "./vendor-inquiries/vendor-inquiries.reducer";
import { drawingReducer } from "./drawing/drawing.reducer";
import { quotesReducer } from "./quotes/quotes.reducer";
import { loadingReducer } from "./loading.reducer";
import { QuoteCreateActions } from "../quote-create/quote/quote-create.actions";

const FULL_LOADING = { initializing: true, summary: true, 'articles.loading': true };

export const quoteReducer = [
    // Initialize
    on(QuoteActions.initializeQuote, QuoteActions.duplicateQuote, QuoteCreateActions.create,
        (state: PagesStore) => ({
            ...state,
            // Preserve quote list filters/sorting/columns; force a reload when returning to the list
            quote: { ...initialQuoteState, quotes: { ...state.quote.quotes, loaded: false } },
        })),

    // Initialize success
    on(QuoteActions.initializeQuoteSuccess, QuoteActions.duplicateQuoteSuccess,
        (state: PagesStore, { quote }) => {
            const latestId = getLatestVersionId(quote.versions);
            const groups = quote.versions.find(v => v.id === latestId)?.itemGroups?.map(g => g.id) ?? [];
            const newQuoteState = {
                ...state.quote,
                ...quote,
                selectedVersion: latestId,
                selectedProductGroupIds: groups,
                expandedGroupIds: groups,
                error: null,
                errorFetchingQuote: false,
            };
            return {
                ...state,
                quote: { ...newQuoteState, showRequirementsDialog: shouldShowRequirementsDialog(newQuoteState) },
            };
        }),

    // Update success (always preserves selectedVersion - patch operations shouldn't change which version user is viewing)
    on(QuoteActions.updateSuccess, (state: PagesStore, { quote }) => {
        const newQuoteState = {
            ...state.quote,
            ...quote,
            selectedVersion: state.quote.selectedVersion,
            error: null,
            errorFetchingQuote: false,
        };
        return {
            ...state,
            quote: { ...newQuoteState, showRequirementsDialog: shouldShowRequirementsDialog(newQuoteState) },
        };
    }),

    // Other quote successes
    on(QuoteActions.createOrderSuccess, QuoteActions.sendSuccess, QuoteActions.updateToUnderReviewSuccess, QuoteActions.updateToReadyForReviewSuccess,
        QuoteActions.updateBackToSentSuccess, QuoteActions.updateMarkAsLostSuccess, QuoteActions.extendValiditySuccess, QuoteActions.changeCustomerSuccess,
        (state: PagesStore, { quote }) => {
            const newQuoteState = {
                ...state.quote,
                ...quote,
                selectedVersion: getLatestVersionId(quote.versions),
                error: null,
                errorFetchingQuote: false,
            };
            return {
                ...state,
                quote: { ...newQuoteState, showRequirementsDialog: shouldShowRequirementsDialog(newQuoteState) },
            };
        }),

    // Credit check (preserve selected version)
    on(CreditCheckActions.runQuoteCheckSuccess, CreditCheckActions.overrideQuoteCheckSuccess,
        (state: PagesStore, { quote }) => ({
            ...state,
            quote: {
                ...state.quote,
                ...quote,
                selectedVersion: state.quote.selectedVersion,
                error: null,
                errorFetchingQuote: false,
            },
        })),

    // Comments (preserve selected version, update comments only)
    on(QuoteActions.createCommentSuccess, QuoteActions.deleteCommentSuccess,
        (state: PagesStore, { quote }) => ({
            ...state,
            quote: {
                ...state.quote,
                comments: quote.comments,
            },
        })),

    // Sales credit (preserve selected version, update salesInfo)
    on(QuoteActions.updateSalesCreditSuccess,
        (state: PagesStore, { quote }) => ({
            ...state,
            quote: {
                ...state.quote,
                salesInfo: quote.salesInfo,
            },
        })),

    // Failures
    on(QuoteActions.initializeQuoteFailure, (state: PagesStore, { error }) => ({
        ...state,
        quote: { ...state.quote, errorFetchingQuote: true, error },
    })),

    on(QuoteCreateActions.createFailure, QuoteActions.duplicateQuoteFailure, QuoteActions.changeCustomerFailure,
        (state: PagesStore, { error }) => ({
            ...state,
            quote: { ...state.quote, error },
        })),

    // Change version (local state change, no API)
    on(QuoteVersionActions.changeVersion, (state: PagesStore, { version }) => ({
        ...state,
        quote: { ...state.quote, selectedVersion: version.id },
    })),

    // Expanded groups (shopping cart UI state)
    on(QuoteVersionActions.setExpandedGroups, (state: PagesStore, { groupIds }) => ({
        ...state,
        quote: { ...state.quote, expandedGroupIds: groupIds },
    })),

    on(QuoteVersionActions.toggleGroupExpanded, (state: PagesStore, { groupId }) => ({
        ...state,
        quote: {
            ...state.quote,
            expandedGroupIds: state.quote.expandedGroupIds.includes(groupId)
                ? state.quote.expandedGroupIds.filter(id => id !== groupId)
                : [...state.quote.expandedGroupIds, groupId],
        },
    })),

    // Product group & article selection (subscription quotes)
    on(ArticlesActions.selectProductGroups, (state: PagesStore, { productGroupIds }) => ({
        ...state,
        quote: { ...state.quote, selectedProductGroupIds: productGroupIds },
    })),

    on(ArticlesActions.selectArticles, (state: PagesStore, { articleIds }) => ({
        ...state,
        quote: { ...state.quote, selectedArticleIds: articleIds },
    })),

    // Child reducers
    ...articlesReducer,
    ...versionReducer,
    ...vendorInquiriesReducer,
    ...drawingReducer,
    ...quotesReducer,
    ...loadingReducer,

    // Loading state
    on(QuoteActions.initializeQuote, QuoteActions.duplicateQuote, QuoteCreateActions.create,
        (state: PagesStore) => ({
            ...state,
            quote: { ...state.quote, loading: FULL_LOADING },
        })),

    on(QuoteActions.changeCustomer, (state: PagesStore) => ({
        ...state,
        quote: { ...state.quote, loading: { ...FULL_LOADING, changeCustomer: true } },
    })),

    // Reset loading on success/failure
    on(QuoteActions.initializeQuoteSuccess, QuoteActions.initializeQuoteFailure, QuoteActions.updateSuccess, QuoteActions.updateFailure,
        QuoteActions.duplicateQuoteSuccess, QuoteActions.duplicateQuoteFailure,
        QuoteActions.changeCustomerSuccess, QuoteActions.changeCustomerFailure,
        QuoteCreateActions.createFailure,
        (state: PagesStore) => ({
            ...state,
            quote: { ...state.quote, loading: {} },
        })),
];
